import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '@aws-amplify/ui-react/styles.css';

// Components
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ReservationList from './pages/ReservationList';
import ReservationForm from './pages/ReservationForm';
import ReservationDetail from './pages/ReservationDetail';
import PropertyList from './pages/PropertyList';
import SitesList from './pages/SitesList';
import GuestInformationFinal from './pages/GuestInformationFinal';
import GuestInformationSuccess from './pages/GuestInformationSuccess';
import GuestInformationView from './pages/GuestInformationView';

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Management Interface Translations
const managementTranslations = {
  en: {
    rental_control: 'Rental Control',
    manage_reservations: 'Manage your rental reservations with ease',
    dashboard: 'Dashboard',
    reservations: 'Reservations',
    new_reservation: 'New Reservation',
    properties: 'Properties',
    booking_sites: 'Booking Sites',
    guest_information: 'Guest Information',
    settings: 'Settings',
    sign_out: 'Sign Out'
  },
  pt: {
    rental_control: 'Controle de Aluguel',
    manage_reservations: 'Gerencie suas reservas de aluguel com facilidade',
    dashboard: 'Painel',
    reservations: 'Reservas',
    new_reservation: 'Nova Reserva',
    properties: 'Propriedades',
    booking_sites: 'Sites de Reserva',
    guest_information: 'Informações do Hóspede',
    settings: 'Configurações',
    sign_out: 'Sair'
  },
  es: {
    rental_control: 'Control de Alquiler',
    manage_reservations: 'Gestiona tus reservas de alquiler con facilidad',
    dashboard: 'Panel',
    reservations: 'Reservas',
    new_reservation: 'Nueva Reserva',
    properties: 'Propiedades',
    booking_sites: 'Sitios de Reserva',
    guest_information: 'Información del Huésped',
    settings: 'Configuraciones',
    sign_out: 'Cerrar Sesión'
  }
};

// AWS Configuration
const awsConfig = {
  aws_project_region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  aws_cognito_region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  aws_user_pools_id: process.env.REACT_APP_USER_POOL_ID,
  aws_user_pools_web_client_id: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  aws_cognito_identity_pool_id: process.env.REACT_APP_IDENTITY_POOL_ID,
  aws_cloud_logic_custom: [
    {
      name: 'ReservationAPI',
      endpoint: process.env.REACT_APP_API_URL,
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1'
    }
  ]
};

Amplify.configure(awsConfig);

// Material-UI Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});

// Custom Authenticator Components
const components = {
  Header() {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <h1>🏠 Controle de Aluguel</h1>
        <p>Gerencie suas reservas de aluguel com facilidade</p>
      </div>
    );
  },
};

const formFields = {
  signUp: {
    given_name: {
      label: 'Nome',
      placeholder: 'Digite seu nome',
      isRequired: true,
      order: 1,
    },
    family_name: {
      label: 'Sobrenome',
      placeholder: 'Digite seu sobrenome',
      isRequired: true,
      order: 2,
    },
    email: {
      label: 'Email',
      placeholder: 'Digite seu email',
      isRequired: true,
      order: 3,
    },
    password: {
      label: 'Senha',
      placeholder: 'Digite sua senha',
      isRequired: true,
      order: 4,
    },
    confirm_password: {
      label: 'Confirmar Senha',
      placeholder: 'Confirme sua senha',
      isRequired: true,
      order: 5,
    },
  },
};

function App() {
  const [language, setLanguage] = useState('pt'); // Default to Portuguese

  const t = (key) => {
    return managementTranslations[language]?.[key] || managementTranslations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public route for guest information collection */}
            <Route path="/guest-info/:reservationId" element={<GuestInformationFinal />} />
            <Route path="/guest-info-success" element={<GuestInformationSuccess />} />
            <Route path="/guest-info-success/:reservationId" element={<GuestInformationSuccess />} />
            
            {/* Protected routes with authentication */}
            <Route path="/*" element={
              <Authenticator
                components={components}
                formFields={formFields}
                signUpAttributes={['given_name', 'family_name', 'email']}
                hideSignUp={true}
              >
                {({ signOut, user }) => (
                  <Layout user={user} signOut={signOut}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/reservations" element={<ReservationList />} />
                      <Route path="/reservations/new" element={<ReservationForm />} />
                      <Route path="/reservations/:id" element={<ReservationDetail />} />
                      <Route path="/reservations/:id/edit" element={<ReservationForm />} />
                      <Route path="/reservations/:id/guest-info" element={<GuestInformationView />} />
                      <Route path="/properties" element={<PropertyList />} />
                      <Route path="/sites" element={<SitesList />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </Layout>
                )}
              </Authenticator>
            } />
          </Routes>
        </Router>
      </ThemeProvider>
    </LanguageContext.Provider>
  );
}

export default App;
